<script setup lang="tsx">
import { ref, onBeforeMount, computed, watch } from 'vue'
import http from '@/services/http'
import configEditor from '@/stores/jira-config-editor'
import type { Response, JiraDomainConfigResponse, JiraDomainConfig } from '@/models/api-generated'
import UserAutocomplete from '@/components/widgets/UserAutocomplete.vue'
import InstanceAutocomplete from '@/components/widgets/InstanceAutocomplete.vue'
import ConfigToolbar from '@/components/jira/ConfigEditToolbar.vue'
import { getTranslatedJiraStatus, viewing } from '@/services/utils'
import AddNewDomain from '@/components/jira/AddNewDomain.vue'
import axios from 'axios'
import { useI18n } from 'vue-i18n'
import { NotInitialized } from '@/models/jira-enums-generated'
import toastStore from '@/stores/toast'
import IconButton from '../shared/IconButton.vue'

const toaster = toastStore()
const model = ref<JiraDomainConfigResponse | null>(null)
const store = configEditor()
const userId = computed(() => sessionMap.user.id)
const originalAdmins = ref<Map<number, number>>(new Map())
const { t } = useI18n()
const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const emit = defineEmits<{
	(e: 'hasChanges', val: boolean): void
}>()

watch(
	() => store.changeCount,
	(newVal) => {
		emit('hasChanges', newVal > 0)
	},
	{ immediate: true },
)

async function fetchDomainConfigs() {
	const { data } = await http.get<Response<JiraDomainConfigResponse>>('api/jira/domainConfigs')
	if (data.data) {
		model.value = data.data
		store.canUserEdit = data.data.configs.some((config) => config.admin?.id === userId.value || userId.value === 1)
		model.value.configs.forEach((config) => {
			if (config.admin?.id) {
				originalAdmins.value.set(config.id, config.admin.id)
			}
			store.watchDomainConfig(config)
		})
	}
}

const canDelete = (domainConfig: JiraDomainConfig) =>
	!domainConfig.isActive && domainConfig.status === NotInitialized && canUserEdit(domainConfig)
const canUserEdit = (domainConfig: JiraDomainConfig) => originalAdmins.value.get(domainConfig.id) === userId.value || userId.value === 1

const instanceId = computed(() => viewing.value || 0)

const toggleActiveFields = (domainConfig: JiraDomainConfig) => {
	if (model.value && domainConfig.isActive) {
		model.value.configs
			.filter((config) => config.instance?.id === domainConfig.instance?.id && config.id !== domainConfig.id)
			.forEach((config) => (config.isActive = false))
	}
}

const isValid = computed(() => {
	if (!model.value) return false
	return model.value.configs.every((domainConfig) => {
		if (!canUserEdit(domainConfig)) return true
		const isValidDomain = domainConfig.domain && domainConfig.admin && domainConfig.instance
		const isActiveValid = domainConfig.isActive ? domainConfig.email && domainConfig.apiToken : true
		return isValidDomain && isActiveValid
	})
})

const getValidationClass = (fieldValue: boolean, isActive: boolean = true) => {
	if (!isActive) return ''
	return fieldValue ? 'is-valid' : 'is-invalid'
}

const getEmailValidationClass = (fieldValue: string, isActive: boolean = true) => {
	if (!isActive) return ''
	if (!fieldValue || !emailPattern.test(fieldValue as string)) {
		return 'is-invalid'
	}
	return 'is-valid'
}

async function deleteDomainConfigs(configID: number) {
	if (confirm(t('jira.confirmDomainConfigDelete'))) {
		const data = await http.delete(`api/jira/deleteDomainConfig/${configID}`)
		if (data.status === axios.HttpStatusCode.Ok) {
			toaster.showSuccess(t('jira.domainDeleted'))
			await fetchDomainConfigs()
		}
	}
}

onBeforeMount(async () => {
	await fetchDomainConfigs()
})
</script>

<template>
	<div class="rounded-container p-3 mt-3">
		<h5>{{ $t('jira.jiraInterface') }}</h5>
		<AddNewDomain @domain-added="fetchDomainConfigs"></AddNewDomain>
		<div class="table-responsive w-100">
			<table class="table">
				<thead>
					<tr>
						<th>{{ $t('jira.domain') }}</th>
						<th v-if="!viewing">{{ $t('institution') }}</th>
						<th>{{ $t('jira.admin') }}</th>
						<th>{{ $t('jira.status.label') }}</th>
						<th>{{ $t('active') }}</th>
						<th>{{ $t('jira.apiToken') }}</th>
						<th>{{ $t('jira.email') }}</th>
						<th>
							{{ $t('jira.automaticImport') }}
							<i class="bi bi-info-circle-fill text-muted ms-2" v-one-tooltip="$t('jira.automaticInfoText')"> </i>
						</th>
						<th></th>
					</tr>
				</thead>
				<tbody>
					<tr v-for="(domainConfig, i) in model?.configs" :key="domainConfig.id" :class="{ odd: i % 2 != 0 }">
						<td v-if="canUserEdit(domainConfig)">
							<input
								:id="'edit-domain-field' + domainConfig.id"
								type="text"
								v-model="domainConfig.domain"
								class="form-control form-control-sm"
								:class="getValidationClass(!!domainConfig.domain)"
							/>
							<div class="invalid-feedback">
								{{ $t('jira.fieldIsRequired') }}
							</div>
						</td>
						<td v-else>{{ domainConfig.domain }}</td>

						<td v-if="canUserEdit(domainConfig) && !viewing">
							<InstanceAutocomplete
								v-if="!viewing"
								:id="'edit-instance-field' + domainConfig.id"
								v-model="domainConfig.instance"
								:class="getValidationClass(!!domainConfig.instance)"
							/>
							<input v-if="instanceId" type="hidden" :value="instanceId" />
							<div class="invalid-feedback">
								{{ $t('jira.fieldIsRequired') }}
							</div>
						</td>
						<td v-else-if="!viewing">{{ domainConfig.instance?.name }}</td>

						<td v-if="canUserEdit(domainConfig)">
							<UserAutocomplete
								:id="'edit-admin-field' + domainConfig.id"
								v-model="domainConfig.admin"
								autocomplete="off"
								:class="getValidationClass(!!domainConfig.admin)"
							/>
							<div class="invalid-feedback">
								{{ $t('jira.fieldIsRequired') }}
							</div>
						</td>
						<td v-else>{{ domainConfig.admin?.label }}</td>
						<td>{{ getTranslatedJiraStatus(domainConfig.status) }}</td>
						<td>
							<div class="col-5 form-check form-switch align-self-center text-start">
								<input
									:id="'edit-active-field' + domainConfig.id"
									class="form-check-input"
									type="checkbox"
									role="switch"
									v-model="domainConfig.isActive"
									:disabled="!canUserEdit(domainConfig)"
									@change="toggleActiveFields(domainConfig)"
								/>
							</div>
						</td>
						<td v-if="canUserEdit(domainConfig)">
							<input
								:id="'edit-token-field' + domainConfig.id"
								v-model="domainConfig.apiToken"
								type="password"
								autocomplete="off"
								class="form-control form-control-sm"
								:class="getValidationClass(!!domainConfig.apiToken, domainConfig.isActive)"
							/>
							<div class="invalid-feedback">
								{{ $t('jira.fieldIsRequired') }}
							</div>
						</td>
						<td v-else>-</td>
						<td v-if="canUserEdit(domainConfig)">
							<input
								:id="'edit-email-field' + domainConfig.id"
								v-model="domainConfig.email"
								type="email"
								class="form-control form-control-sm"
								:class="getEmailValidationClass(domainConfig.email, domainConfig.isActive)"
							/>
							<div class="invalid-feedback">
								{{ $t('jira.invalidEmail') }}
							</div>
						</td>
						<td v-else>-</td>
						<td>
							<div class="col-5 form-check form-switch align-self-center text-start">
								<input
									:id="'edit-automatic-import-field' + domainConfig.id"
									class="form-check-input"
									type="checkbox"
									role="switch"
									v-model="domainConfig.automaticImport"
									:disabled="!canUserEdit(domainConfig)"
								/>
							</div>
						</td>
						<td>
							<IconButton
								v-if="canDelete(domainConfig)"
								:id="'delete-domain-config' + domainConfig.id"
								class="bi bi-trash"
								@click="deleteDomainConfigs(domainConfig.id)"
								:title="$t('misc.delete')"
								size="fs-5"
							/>
						</td>
					</tr>
				</tbody>
			</table>
		</div>
		<input name="disable-pwd-cfg-1" type="password" id="disable-pwd-cfg-1" style="display: none" value="disable-pwd-cfg-1" />
		<input name="disable-pwd-cfg-2" type="password" id="disable-pwd-cfg-2" style="display: none" value="disable-pwd-cfg-2" />
		<input name="disable-pwd-cfg-3" type="password" id="disable-pwd-cfg-3" style="display: none" value="disable-pwd-cfg-3" />
	</div>
	<ConfigToolbar :isValid="isValid" @domain-edited="fetchDomainConfigs" />
</template>
